/**
 * Exemplos de preenchimento do briefing, por campo.
 *
 * Não são sugestões geradas: cada um saiu de um briefing real do método do
 * Fabio (Guimatex, OnlinePort, Patrimonial, SOS Monitoramento), justamente
 * para ensinar o formato e o tom que a IA espera ler. Clicar num exemplo
 * escreve o texto no campo, e ele continua editável.
 *
 * ⛔ Exemplo não é conteúdo pronto para mandar: quem usa precisa trocar pelos
 * dados do cliente da vez. Por isso os exemplos vêm marcados com o cliente de
 * origem e nenhum deles é genérico o bastante para passar despercebido numa
 * proposta.
 */
export interface ExemploCampo {
  /** De onde veio, mostrado como etiqueta. */
  tag: string;
  texto: string;
}

export const BRIEFING_EXEMPLOS: Record<string, ExemploCampo[]> = {
  'descoberta.oQueFaz': [
    { tag: 'Indústria', texto: 'Indústria têxtil desde 1979: fabrica malhas, tecidos e uniformes e vende para atacado, varejo e confecções em seis estados.' },
    { tag: 'Portaria', texto: 'Portaria remota e presencial, controle de acesso e zeladoria para condomínios e empresas.' },
    { tag: 'Monitoramento', texto: 'Central de monitoramento eletrônico e segurança privada.' },
  ],
  'descoberta.dorFrase': [
    { tag: 'WhatsApp', texto: '“Cada loja tem um celular, não sabem com quem conversam, tudo descentralizado, podem estar perdendo clientes, não tem histórico.”' },
    { tag: 'Voz', texto: '“O PABX só cai, não aguento mais, às vezes fica horas sem funcionar nossa portaria, perdemos clientes.”' },
    { tag: 'Voz', texto: '“A central cai e a gente não sabe.”' },
  ],
  'descoberta.usamHoje': [
    { tag: 'WhatsApp', texto: 'WhatsApp comum, um celular por loja, sem centralização.' },
    { tag: 'Voz', texto: 'PABX físico de fornecedor terceiro; qual, não informado.' },
    { tag: 'Já é cliente', texto: 'Já é cliente IPCOM de telefonia, contrato ativo. O WhatsApp é o que ainda está fora do Hub.' },
  ],
  'descoberta.incidente': [
    { tag: 'Vivido', texto: 'Quedas recorrentes, de horas, e já perderam clientes por causa disso.' },
    { tag: 'Vivido', texto: 'Quedas da central sem aviso; a equipe só descobre quando o cliente reclama.' },
    { tag: 'Sem incidente', texto: 'Não relatou bloqueio nem queda: a dor é a perda silenciosa de cliente por falta de histórico.' },
  ],
  'descoberta.numerosDitos': [
    { tag: 'WhatsApp', texto: '36 atendentes, 1 número de WhatsApp, várias lojas com um celular cada. Volume de mensagens: não informado.' },
    { tag: 'Voz', texto: '300 ramais nas guaritas, 12 usuários na central, 10 canais e 1 número para portar.' },
    { tag: 'Dado vago', texto: 'Cerca de 400 mensagens por mês, foi como o cliente falou.' },
  ],
  'descoberta.decisores': [
    { tag: 'Sócio', texto: 'Rudi Guimarães, sócio proprietário, não técnico.' },
    { tag: 'Dois', texto: 'O sócio-diretor decide; o TI opina na migração.' },
  ],
  'descoberta.preocupacoes': [
    { tag: 'Migração', texto: 'Migrar sem parar o atendimento, e a equipe não é técnica.' },
    { tag: 'Preço', texto: 'Preço e prazo: já teve promessa não cumprida por fornecedor.' },
  ],
  'descoberta.valoriza': [
    { tag: 'Não parar', texto: 'Não parar: portaria muda é cliente perdido, e a operação é 24 horas.' },
    { tag: 'Controle', texto: 'Centralizar e ter histórico: saber quem atendeu quem e quem está esperando.' },
  ],
  'oferta.pacoteRecomendado': [
    { tag: 'Voz', texto: 'Voz com 1000 ramais, 30 canais simultâneos e 9 números diretos, por ser uma central de monitoramento que não pode parar.' },
    { tag: 'Expansão', texto: 'O WhatsApp entra no mesmo Hub onde a voz dele já roda, com contrato e conta únicos.' },
    { tag: 'WhatsApp', texto: 'Combo de 36 posições: uma fila só para as lojas, com histórico por atendente.' },
  ],
  'dimensionamento.waNumeroAtualDeQuem': [
    { tag: 'Empresa', texto: 'Empresa' },
    { tag: 'Pessoa', texto: 'Pessoa: hoje é o celular do sócio' },
  ],
  'dimensionamento.waVolumeMensagensMes': [
    { tag: 'Dado vago', texto: 'Cerca de 400 por mês, foi como o cliente falou' },
    { tag: 'Sem dado', texto: 'Não informado' },
  ],
  'dimensionamento.waQuemDispara': [
    { tag: 'Equipe', texto: 'A equipe, pelo aplicativo' },
    { tag: 'Sistema', texto: 'O sistema, por integração' },
  ],
  'dimensionamento.vozChamadasSimultaneasPico': [
    { tag: 'Número', texto: '30' },
    { tag: 'Dado vago', texto: '10 canais, foi o que o cliente estimou' },
  ],
  'dimensionamento.vozProvedorAtual': [
    { tag: 'Operadora', texto: 'Vivo' },
    { tag: 'Sem dado', texto: 'Fornecedor terceiro, nome não informado' },
  ],
  'dimensionamento.vozNumerosPortar': [
    { tag: 'Vários', texto: '9 números diretos, além do incluso no pacote' },
    { tag: 'Um', texto: '1 portabilidade; o número do pacote não conta' },
  ],
  'dimensionamento.vozPabxFisicoContrato': [
    { tag: 'Sem multa', texto: 'PABX físico próprio, sem contrato vigente' },
    { tag: 'A confirmar', texto: 'Contrato vigente com multa: a confirmar com o cliente' },
  ],
  'dimensionamento.vozRamaisSemUsuarioOnde': [
    { tag: 'Portaria', texto: '300 ramais nas guaritas e portarias dos condomínios' },
    { tag: 'Escritório', texto: 'Portaria, recepção e 4 salas de reunião' },
  ],
  'provas.caseEspelho': [
    { tag: 'Segurança', texto: 'Grupo Vigivel Segurança' },
    { tag: 'Saúde', texto: 'Oftalmoclínica Cascavel' },
  ],
  'provas.caseEspelhoUsa': [
    { tag: 'Segurança', texto: 'Telefonia IPCOM com integração ao Segware Sigma Cloud' },
    { tag: 'Saúde', texto: 'Telefonia para o agendamento de pacientes' },
  ],
  'condicoes.prazoImplantacao': [
    { tag: 'Sem confirmação', texto: 'Definido no agendamento da virada' },
    { tag: 'Confirmado', texto: 'Até 2 dias úteis' },
  ],
};
